import React from 'react';
import {
  ActivityIndicator,
  Dimensions,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { COLORS } from '../constants/theme';
import { useFavouritesWatchlist } from '../hooks/useFavourites';
import { OwnershipScope, WatchlistItem } from '../types/favourites';
import { FavouriteToggleButton } from './FavouriteToggleButton';
import { PressableCard } from './PressableCard';
import { SkeletonCard } from './SkeletonLoader';

const { width } = Dimensions.get('window');
const isDesktop = width >= 1024;

interface FavouritesWatchlistProps {
  readonly ownershipScope?: OwnershipScope;
  readonly onProductPress?: (isin: string) => void;
}

const formatPrice = (value: number | null): string => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '-';
  }
  return Number(value).toFixed(2);
};

const WatchlistRow: React.FC<{
  readonly item: WatchlistItem;
  readonly ownershipScope: OwnershipScope;
  readonly onPress?: (isin: string) => void;
}> = ({ item, ownershipScope, onPress }) => {
  const content = (
    <>
      <View style={styles.favouriteCell}>
        <FavouriteToggleButton isin={item.isin} ownershipScope={ownershipScope} size="sm" />
      </View>

      <View style={styles.mainCell}>
        <Text style={[styles.nameText, item.unavailable && styles.unavailableText]} numberOfLines={2}>
          {item.unavailable ? item.isin : item.name || item.isin}
        </Text>
        <Text style={styles.metaText}>
          {item.isin}
          {item.issuer ? ` · ${item.issuer}` : ''}
          {item.unavailable ? ' · Unavailable' : ''}
        </Text>
      </View>

      <View style={styles.priceCell}>
        <Text style={styles.priceLabel}>Bid</Text>
        <Text style={[styles.priceValue, item.unavailable && styles.unavailableText]}>
          {item.unavailable ? '-' : formatPrice(item.bid)}
        </Text>
      </View>

      <View style={styles.priceCell}>
        <Text style={styles.priceLabel}>Ask</Text>
        <Text style={[styles.priceValue, item.unavailable && styles.unavailableText]}>
          {item.unavailable ? '-' : formatPrice(item.ask)}
        </Text>
      </View>

      <View style={styles.currencyCell}>
        <Text style={styles.priceLabel}>CCY</Text>
        <Text style={[styles.currencyValue, item.unavailable && styles.unavailableText]}>
          {item.unavailable ? '-' : item.currency || '-'}
        </Text>
      </View>

      {!isDesktop && <Text style={styles.arrowIcon}>›</Text>}
    </>
  );

  if (item.unavailable) {
    return <View style={[styles.row, styles.rowUnavailable]}>{content}</View>;
  }

  return (
    <PressableCard
      style={styles.row}
      onPress={() => onPress?.(item.isin)}
      pressedScale={0.98}
    >
      {content}
    </PressableCard>
  );
};

export const FavouritesWatchlist: React.FC<FavouritesWatchlistProps> = ({
  ownershipScope = 'group',
  onProductPress,
}) => {
  const { data, isLoading, isError, refetch, isFetching } = useFavouritesWatchlist(ownershipScope);
  const items = data ?? [];

  if (isLoading) {
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Watchlist</Text>
        <SkeletonCard />
      </View>
    );
  }

  if (isError) {
    return (
      <View style={styles.section}>
        <View style={styles.headerRow}>
          <Text style={styles.sectionTitle}>Watchlist</Text>
          <TouchableOpacity onPress={() => refetch()} activeOpacity={0.7}>
            <Text style={styles.retryText}>Retry</Text>
          </TouchableOpacity>
        </View>
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>Could not load your watchlist.</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.section}>
      <View style={styles.headerRow}>
        <Text style={styles.sectionTitle}>Watchlist</Text>
        {isFetching ? <ActivityIndicator size="small" color={COLORS.primary} /> : null}
      </View>

      {items.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyTitle}>No favourites yet</Text>
          <Text style={styles.emptyText}>
            Tap the star on a product in Portfolio or Product details to add it here.
          </Text>
        </View>
      ) : (
        <View style={styles.list}>
          {isDesktop && (
            <View style={styles.tableHeader}>
              <Text style={[styles.headerCell, styles.favouriteHeader]} />
              <Text style={[styles.headerCell, styles.mainHeader]}>Product</Text>
              <Text style={[styles.headerCell, styles.priceHeader]}>Bid</Text>
              <Text style={[styles.headerCell, styles.priceHeader]}>Ask</Text>
              <Text style={[styles.headerCell, styles.currencyHeader]}>CCY</Text>
            </View>
          )}
          {items.map((item) => (
            <WatchlistRow
              key={`${item.favouriteId}-${item.isin}`}
              item={item}
              ownershipScope={ownershipScope}
              onPress={onProductPress}
            />
          ))}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    width: '100%',
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: isDesktop ? 16 : 15,
    fontWeight: '600',
    color: '#1F2937',
    fontFamily: 'Montserrat_600SemiBold',
  },
  retryText: {
    fontSize: 13,
    color: COLORS.primary,
    fontWeight: '600',
  },
  list: {
    backgroundColor: COLORS.white,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: '#F9FAFB',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  headerCell: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6B7280',
    fontFamily: 'Montserrat_600SemiBold',
  },
  favouriteHeader: {
    width: 36,
  },
  mainHeader: {
    flex: 1,
    paddingRight: 8,
  },
  priceHeader: {
    width: 72,
    textAlign: 'right',
  },
  currencyHeader: {
    width: 48,
    textAlign: 'right',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
    backgroundColor: COLORS.white,
  },
  rowUnavailable: {
    opacity: 0.75,
    backgroundColor: '#F9FAFB',
  },
  favouriteCell: {
    width: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mainCell: {
    flex: 1,
    paddingRight: 8,
  },
  nameText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#1F2937',
    fontFamily: 'Montserrat_600SemiBold',
    marginBottom: 2,
  },
  metaText: {
    fontSize: 12,
    color: '#6B7280',
    fontFamily: 'Montserrat_400Regular',
  },
  unavailableText: {
    color: '#9CA3AF',
  },
  priceCell: {
    width: isDesktop ? 72 : 58,
    alignItems: 'flex-end',
  },
  currencyCell: {
    width: isDesktop ? 48 : 40,
    alignItems: 'flex-end',
  },
  priceLabel: {
    fontSize: 10,
    color: '#9CA3AF',
    marginBottom: 2,
    fontFamily: 'Montserrat_400Regular',
  },
  priceValue: {
    fontSize: 13,
    fontWeight: '600',
    color: '#1F2937',
    fontFamily: 'Montserrat_600SemiBold',
  },
  currencyValue: {
    fontSize: 12,
    fontWeight: '500',
    color: '#374151',
    fontFamily: 'Montserrat_500Medium',
  },
  arrowIcon: {
    marginLeft: 6,
    fontSize: 18,
    color: '#9CA3AF',
  },
  emptyContainer: {
    backgroundColor: COLORS.white,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 20,
  },
  emptyTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 6,
    fontFamily: 'Montserrat_600SemiBold',
  },
  emptyText: {
    fontSize: 13,
    color: '#6B7280',
    lineHeight: 18,
    fontFamily: 'Montserrat_400Regular',
  },
});
